import { Suspense, useEffect } from "react";
import { Box } from "@mui/material";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchConcepts, selectLensEditorLens, selectMissingEntityIds } from "redux/slices/lensEditor";
import LoadingPattern from "components/LoadingPattern";
import { Await, Outlet, useLoaderData } from "react-router";
import PatternError from "components/PatternError";
import { LensRefreshCallbackType, LensRefreshAlertProvider } from "contexts/LensRefreshAlertContext";
import { useSnackbar, SnackbarProvider } from "notistack";
import { LensUpdateSnackbarProvider } from "contexts/LensUpdateSnackbarContext";
import {
  ErrorSnackbar,
  InfoSnackbar,
  LensEditorSendAlertSnackbar,
  FormChageSnackbar,
  SuccessSnackbar,
  MultilineErrorSnackbar,
} from "helpers/Snackbars";

declare module "notistack" {
  interface VariantOverrides {
    lensUpdate: {
      callback: () => Promise<any>;
    };
    lensEditorAlert: {
      onCancel: () => Promise<any>;
      onSend: () => Promise<any>;
    };
  }
}

export type LensEditorTab = "lens_rules" | "profile" | "duplicates" | "case_reports" | "case_properties" | "ai_chats";

const LensEditor = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const missingEntityIds = useAppSelector(selectMissingEntityIds);

  const data = useLoaderData() as { load: Promise<void> };
  const lens = useAppSelector(selectLensEditorLens);

  const lensRefreshCallback: LensRefreshCallbackType = ({ lens }) => {
    enqueueSnackbar(`Users viewing ${lens.name} have been notified to refresh their lens`, {
      variant: "info",
      persist: false,
    });
  };

  useEffect(() => {
    dispatch(fetchConcepts({ conceptIds: new Array(...missingEntityIds.keys()) }));
  }, [missingEntityIds]);

  return (
    <LensUpdateSnackbarProvider>
      <SnackbarProvider
        maxSnack={3}
        Components={{
          info: InfoSnackbar,
          success: SuccessSnackbar,
          error: ErrorSnackbar,
          lensUpdate: FormChageSnackbar,
          lensEditorAlert: LensEditorSendAlertSnackbar,
          multilineError: MultilineErrorSnackbar,
        }}>
        <LensRefreshAlertProvider lens={lens} defaultCallback={lensRefreshCallback}>
          <Suspense fallback={<LoadingPattern centered />}>
            <Await resolve={data.load} errorElement={<PatternError />}>
              <Box
                sx={{
                  minHeight: 0,
                  display: "flex",
                  width: "100%",
                  height: "100%",
                }}>
                <Box sx={{ overflowY: "auto", width: "100%", pt: 1, height: "100%" }}>
                  <Outlet />
                </Box>
              </Box>
            </Await>
          </Suspense>
        </LensRefreshAlertProvider>
      </SnackbarProvider>
    </LensUpdateSnackbarProvider>
  );
};

export default LensEditor;
